/* Kanful 関連商品（タブ型）
 * - タブ切替
 * - 各タブの商品カードを検索の代替テンプレート（view=cards。templates/search.cards.liquid）から取得して差し込む（Issue #226）。
 *   Liquid で collections.all を走査する旧方式は for の 50 周上限で先頭 50 件しか見ておらず、ほぼ常に空だった。
 *   取得結果から自分自身（data-kf-related-exclude）を除き、表示件数（data-kf-related-limit）に切る。
 *   0 件なら空メッセージを出し、そのタブのボタンを隠す（両方 0 件ならセクションごと隠す）。
 *   一過性の失敗は間隔を置いて 2 回までやり直す。それでも失敗なら 0 件とは区別して「読み込めませんでした」を出し、
 *   タブは隠さない（存在する商品を「無い」と伝えないため）。テーマエディタの再描画（shopify:section:load）でも取得する。
 *
 * テスト（tests/related-products.test.mjs）から vm で評価できるよう、window.KanfulRelated に関数を露出する。
 */
(function (root) {
  'use strict';

  var RETRY_MAX = 2;
  /* やり直しの間隔（ms × 回数）。無遅延で連射すると 429 / 5xx の局面でパネル 2 枚 × 3 回を瞬時に投げる */
  var RETRY_BASE_MS = 300;

  /* 取得した HTML（kf-related__slot の並び）を DOM にし、自分自身を除いて limit 件に切った要素の配列を返す */
  function pickSlots(html, excludeId, limit, doc) {
    var d = doc || root.document;
    var wrap = d.createElement('div');
    wrap.innerHTML = html;
    var slots = Array.prototype.slice.call(wrap.querySelectorAll('[data-kf-product-id]'));
    var out = [];
    for (var i = 0; i < slots.length; i++) {
      if (String(slots[i].getAttribute('data-kf-product-id')) === String(excludeId)) continue;
      out.push(slots[i]);
      if (out.length >= limit) break;
    }
    return out;
  }

  /* グリッドへ差し込む。返り値は差し込んだ件数 */
  function applyCards(grid, slots) {
    grid.innerHTML = '';
    for (var i = 0; i < slots.length; i++) grid.appendChild(slots[i]);
    grid.setAttribute('aria-busy', 'false');
    return slots.length;
  }

  function fetchHtml(url, fetchImpl, attempt, sleep) {
    var wait = sleep || function (ms) { return new Promise(function (r) { setTimeout(r, ms); }); };
    return fetchImpl(url, { credentials: 'same-origin' })
      .then(function (res) {
        if (!res.ok) throw new Error('HTTP ' + res.status);
        return res.text();
      })
      .catch(function (err) {
        if (attempt >= RETRY_MAX) throw err;
        return wait(RETRY_BASE_MS * (attempt + 1)).then(function () {
          return fetchHtml(url, fetchImpl, attempt + 1, sleep);
        });
      });
  }

  /* 1 パネルぶんの取得と反映。resolve 値は表示件数。取得失敗は -1（0 件と区別し、タブを隠さない） */
  function loadPanel(panel, opts) {
    var grid = panel.querySelector('[data-kf-related-grid]');
    var empty = panel.querySelector('[data-kf-related-empty]');
    var error = panel.querySelector('[data-kf-related-error]');
    if (!grid) return Promise.resolve(0);
    var url = grid.getAttribute('data-kf-related-src');
    var doc = opts.document;
    return fetchHtml(url, opts.fetch, 0, opts.sleep)
      .then(function (html) {
        var n = applyCards(grid, pickSlots(html, opts.excludeId, opts.limit, doc));
        if (empty) empty.hidden = n > 0;
        if (error) error.hidden = true;
        return n;
      })
      .catch(function () {
        grid.innerHTML = '';
        grid.setAttribute('aria-busy', 'false');
        if (empty) empty.hidden = true;
        if (error) error.hidden = false;
        return -1;
      });
  }

  function initTabs(section) {
    var tabs = section.querySelectorAll('[data-kf-tab-target]');
    var panels = section.querySelectorAll('[data-kf-tab-panel]');

    function show(target) {
      tabs.forEach(function (t) {
        var active = t.dataset.kfTabTarget === target;
        t.classList.toggle('is-active', active);
        t.setAttribute('aria-selected', active ? 'true' : 'false');
      });
      panels.forEach(function (p) {
        var active = p.dataset.kfTabPanel === target;
        p.classList.toggle('kf-related__panel--hidden', !active);
      });
    }

    tabs.forEach(function (tab) {
      tab.addEventListener('click', function () {
        show(tab.dataset.kfTabTarget);
      });
    });
    return show;
  }

  /* 0 件のタブを隠し、表示中のタブが 0 件なら残る方へ切り替える。全タブ 0 件ならセクションを隠す。
     取得失敗（-1）のタブは「無い」と断定できないので残す */
  function settleTabs(section, counts, show) {
    var remaining = [];
    Object.keys(counts).forEach(function (key) {
      var tab = section.querySelector('[data-kf-tab-target="' + key + '"]');
      if (counts[key] !== 0) {
        remaining.push(key);
      } else if (tab) {
        tab.hidden = true;
      }
    });
    if (remaining.length === 0) {
      section.hidden = true;
      return;
    }
    var active = section.querySelector('[data-kf-tab-target].is-active');
    if (!active || counts[active.dataset.kfTabTarget] === 0) show(remaining[0]);
    if (remaining.length === 1) {
      /* タブが 1 つなら操作対象が無いので tablist を隠し、孤児になる tabpanel の role も外す */
      var tablist = section.querySelector('[role="tablist"]');
      if (tablist) tablist.hidden = true;
      var panel = section.querySelector('[data-kf-tab-panel="' + remaining[0] + '"]');
      if (panel && panel.removeAttribute) panel.removeAttribute('role');
    }
  }

  function init(doc, fetchImpl) {
    var d = doc || root.document;
    var section = d.querySelector('[data-kf-related]');
    if (!section) return Promise.resolve(null);
    var show = initTabs(section);
    var limit = parseInt(section.getAttribute('data-kf-related-limit'), 10) || 4;
    var excludeId = section.getAttribute('data-kf-related-exclude');
    var panels = Array.prototype.slice.call(section.querySelectorAll('[data-kf-tab-panel]'));
    if (!fetchImpl && typeof root.fetch !== 'function') return Promise.resolve(null);
    var f = fetchImpl || root.fetch.bind(root);
    return Promise.all(
      panels.map(function (panel) {
        return loadPanel(panel, { fetch: f, document: d, excludeId: excludeId, limit: limit }).then(function (n) {
          return [panel.dataset.kfTabPanel, n];
        });
      })
    ).then(function (pairs) {
      var counts = {};
      pairs.forEach(function (pr) {
        counts[pr[0]] = pr[1];
      });
      settleTabs(section, counts, show);
      return counts;
    });
  }

  root.KanfulRelated = { pickSlots: pickSlots, applyCards: applyCards, loadPanel: loadPanel, settleTabs: settleTabs, init: init, RETRY_MAX: RETRY_MAX, RETRY_BASE_MS: RETRY_BASE_MS };

  if (root.document && !root.__KANFUL_RELATED_NO_AUTO__) {
    if (root.document.readyState === 'loading') {
      root.document.addEventListener('DOMContentLoaded', function () {
        init();
      });
    } else {
      init();
    }
    /* テーマエディタでセクションを再描画したとき（defer スクリプトは再実行されない） */
    root.document.addEventListener('shopify:section:load', function (ev) {
      if (ev.target && ev.target.querySelector && ev.target.querySelector('[data-kf-related]')) init();
    });
  }
})(typeof window !== 'undefined' ? window : this);
